import React from 'react';

import type {
  AmbientBloomProps,
  AmbientBloomSize,
  AmbientBloomStrength,
  AmbientBloomTone,
} from './AmbientBloom.types';

/**
 * Diameter, with the blur that keeps it edgeless at that size. The blurs sit
 * past the scale's largest (`blur-3xl`, 64px): a bloom this wide needs more.
 */
const SIZE_CLASSES: Record<AmbientBloomSize, string> = {
  sm: 'size-75 blur-[80px]',
  md: 'size-100 blur-[100px]',
  lg: 'size-125 blur-[120px]',
  xl: 'size-150 blur-[120px]',
};

const TONE_CLASSES: Record<AmbientBloomTone, string> = {
  'primary-400': 'from-primary-400',
  'primary-500': 'from-primary-500',
};

const STRENGTH_CLASSES: Record<AmbientBloomStrength, string> = {
  faint: 'opacity-3',
  mild: 'opacity-4',
  soft: 'opacity-5',
};

/**
 * AmbientBloom - a soft sakura glow in the dark, behind a page or a section:
 * a radial fade from the tone to nothing, blurred until it has no edge.
 * Decorative, so hidden from assistive tech and from the pointer. Blooms come
 * in pairs at opposite corners; place each with `className`.
 *
 * @example
 * <AmbientBloom size="xl" className="fixed -left-40 -top-40" />
 * <AmbientBloom size="lg" tone="primary-400" className="fixed -bottom-40 -right-40" />
 */
export function AmbientBloom({
  size,
  tone = 'primary-500',
  strength = 'faint',
  className,
}: AmbientBloomProps): React.JSX.Element {
  return (
    <div
      className={`pointer-events-none rounded-full bg-radial to-transparent to-70% ${TONE_CLASSES[tone]} ${SIZE_CLASSES[size]} ${STRENGTH_CLASSES[strength]} ${className}`}
      aria-hidden="true"
    />
  );
}

export default AmbientBloom;
